import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { baseUrl } from "../../../services/setup";
import { Toast } from "../../../components/errorNotifier";
import { loader } from "../../../components/LoadingSpinner";

const formatMoney = (val) => {
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 2,
  }).format(Number(val || 0));
};

const formatDate = (val) => {
  if (!val) return "N/A";
  const d = new Date(String(val).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? val : d.toLocaleString();
};

const SettlementManagement = () => {
  // Navigation / active tab
  const [activeTab, setActiveTab] = useState("queue"); // "queue" | "exceptions" | "history"

  // Data states
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [overview, setOverview] = useState({
    unsettled_amount: 0,
    unsettled_count: 0,
    automated_settled_amount: 0,
    manual_settled_amount: 0,
    failed_transfers_count: 0,
    processing_transfers_count: 0,
  });
  const [unsettledQueue, setUnsettledQueue] = useState([]);
  const [historyData, setHistoryData] = useState({
    total: 0,
    rows: [],
    limit: 50,
    offset: 0,
  });
  const [accounts, setAccounts] = useState([]);

  // Filters for History Tab
  const [historyFilters, setHistoryFilters] = useState({
    method: "all",
    status: "all",
    account_id: "",
    search: "",
    limit: 50,
    offset: 0,
  });

  // Modal States
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualForm, setManualForm] = useState({
    account_id: "",
    account_title: "",
    bank_name: "",
    account_number: "",
    amount: "",
    bank_reference: "",
    transfer_reference: "",
    notes: "",
    settled_by: "",
    transfer_date: new Date().toISOString().slice(0, 16),
    payment_ids: [],
  });

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTransferRef, setSelectedTransferRef] = useState(null);
  const [transferDetails, setTransferDetails] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Active user / officer email
  const officerEmail =
    localStorage.getItem("userId") ||
    localStorage.getItem("email") ||
    "Finance Officer";

  // Initial fetch
  useEffect(() => {
    fetchOverview();
    fetchUnsettledQueue();
    fetchAccounts();
    fetchHistory();
  }, []);

  const fetchOverview = async () => {
    try {
      const resp = await axios.get(`${baseUrl}settlement/overview`);
      if (resp.data?.status === "success") {
        setOverview(resp.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch settlement overview:", err);
    }
  };

  const fetchUnsettledQueue = async () => {
    try {
      const resp = await axios.get(`${baseUrl}settlement/unsettled_queue`);
      if (resp.data?.status === "success") {
        setUnsettledQueue(resp.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch unsettled queue:", err);
    }
  };

  const fetchAccounts = async () => {
    try {
      const resp = await axios.get(`${baseUrl}settlement/accounts`);
      if (resp.data?.status === "success") {
        setAccounts(resp.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch settlement accounts:", err);
    }
  };

  const fetchHistory = useCallback(
    async (overrideFilters = {}) => {
      setLoading(true);
      try {
        const query = { ...historyFilters, ...overrideFilters };
        const resp = await axios.get(`${baseUrl}settlement/history`, {
          params: query,
        });
        if (resp.data?.status === "success") {
          setHistoryData(
            resp.data.data || { total: 0, rows: [], limit: 50, offset: 0 }
          );
        }
      } catch (err) {
        console.error("Failed to fetch settlement history:", err);
        Toast.fire({ icon: "error", title: "Failed to load settlement history" });
      } finally {
        setLoading(false);
      }
    },
    [historyFilters]
  );

  const refreshAll = async () => {
    setLoading(true);
    loader({ title: "Refreshing Settlement Data", text: "Please wait..." });
    await Promise.all([
      fetchOverview(),
      fetchUnsettledQueue(),
      fetchAccounts(),
      fetchHistory(),
    ]);
    setLoading(false);
    Toast.fire({ icon: "success", title: "Settlement data refreshed" });
  };

  // Open manual transfer modal
  const openManualModalForQueueItem = (group) => {
    setManualForm({
      account_id: String(group.account_id),
      account_title: group.account_title || "",
      bank_name: group.bank_name || "",
      account_number: group.account_number || "",
      amount: String(group.total_amount || ""),
      bank_reference: "",
      transfer_reference: group.last_failed?.transfer_reference || "",
      notes: group.last_failed
        ? `Manual settlement following automated failure: ${group.last_failed.reason}`
        : "Direct bank transfer executed manually by Finance Office.",
      settled_by: officerEmail,
      transfer_date: new Date().toISOString().slice(0, 16),
      payment_ids: group.payment_ids || [],
    });
    setShowManualModal(true);
  };

  const openManualModalBlank = () => {
    setManualForm({
      account_id: accounts.length ? String(accounts[0].id) : "",
      account_title: accounts.length
        ? accounts[0].acc_title || accounts[0].name
        : "",
      bank_name: accounts.length ? accounts[0].bank_name : "",
      account_number: accounts.length ? accounts[0].account_number : "",
      amount: "",
      bank_reference: "",
      transfer_reference: "",
      notes: "Direct bank transfer executed manually by Finance Office.",
      settled_by: officerEmail,
      transfer_date: new Date().toISOString().slice(0, 16),
      payment_ids: [],
    });
    setShowManualModal(true);
  };

  const handleAccountSelectChange = (accId) => {
    const acc = accounts.find((a) => String(a.id) === String(accId));
    if (acc) {
      // Check if this account has pending items in unsettledQueue
      const queued = unsettledQueue.find((q) => String(q.account_id) === String(accId));
      setManualForm((prev) => ({
        ...prev,
        account_id: String(acc.id),
        account_title: acc.acc_title || acc.name || "",
        bank_name: acc.bank_name || "",
        account_number: acc.account_number || "",
        amount: queued ? String(queued.total_amount) : prev.amount,
        payment_ids: queued ? queued.payment_ids : [],
      }));
    } else {
      setManualForm((prev) => ({
        ...prev,
        account_id: accId,
      }));
    }
  };

  // Submit manual transfer
  const handleSubmitManualSettlement = async (e) => {
    e.preventDefault();
    if (!manualForm.account_id) {
      Toast.fire({ icon: "warning", title: "Please select a settlement account." });
      return;
    }
    if (!manualForm.bank_reference.trim()) {
      Toast.fire({
        icon: "warning",
        title: "Bank reference / teller number is required for manual settlement audit.",
      });
      return;
    }
    if (Number(manualForm.amount) <= 0) {
      Toast.fire({ icon: "warning", title: "Settlement amount must be greater than zero." });
      return;
    }

    try {
      setActionLoading(true);
      loader({
        title: "Recording Manual Settlement...",
        text: "Synchronizing accounting records & locking out duplicate automated runs...",
      });

      const payload = {
        account_id: Number(manualForm.account_id),
        bank_name: manualForm.bank_name.trim(),
        bank_reference: manualForm.bank_reference.trim(),
        amount: Number(manualForm.amount),
        payment_ids: manualForm.payment_ids,
        transfer_reference: manualForm.transfer_reference.trim(),
        notes: manualForm.notes.trim(),
        settled_by: manualForm.settled_by || officerEmail,
        transfer_date: manualForm.transfer_date,
      };

      const resp = await axios.post(`${baseUrl}settlement/record_manual`, payload);
      if (resp.data?.status === "success") {
        Toast.fire({
          icon: "success",
          title: resp.data.message || "Manual settlement recorded successfully!",
        });
        setShowManualModal(false);
        // Refresh all queues and stats
        await Promise.all([
          fetchOverview(),
          fetchUnsettledQueue(),
          fetchHistory(),
        ]);
      } else {
        throw new Error(resp.data?.message || "Failed to record manual settlement.");
      }
    } catch (err) {
      console.error("Error recording manual settlement:", err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to record manual settlement.";
      Toast.fire({ icon: "error", title: msg });
    } finally {
      setActionLoading(false);
    }
  };

  // Trigger automated Paystack transfer
  const handleTriggerAutomated = async (accountId = null, accountTitle = "All Accounts") => {
    const isSingle = accountId !== null;
    const confirmPrompt = isSingle
      ? `Are you sure you want to trigger automated Paystack transfer for "${accountTitle}"?`
      : "Are you sure you want to trigger automated Paystack transfer for ALL pending accounts?";

    if (!window.confirm(confirmPrompt)) {
      return;
    }

    try {
      setActionLoading(true);
      loader({
        title: "Initiating Paystack Transfer...",
        text: "Communicating with Paystack transfer service...",
      });

      const payload = accountId ? { account_id: accountId } : {};
      const resp = await axios.post(`${baseUrl}settlement/trigger_automated`, payload);

      if (resp.data?.status === "success") {
        const results = resp.data.results || [];
        const hasFailed = results.some((r) => r.status === "failed" || r.status === "error");

        if (hasFailed) {
          Toast.fire({
            icon: "warning",
            title: "Some transfers failed or require manual settlement. Check Exceptions.",
          });
        } else {
          Toast.fire({
            icon: "success",
            title: resp.data.message || "Automated settlement completed successfully!",
          });
        }

        await Promise.all([
          fetchOverview(),
          fetchUnsettledQueue(),
          fetchHistory(),
        ]);
      } else {
        throw new Error(resp.data?.message || "Automated settlement failed.");
      }
    } catch (err) {
      console.error("Automated settlement error:", err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Automated settlement run failed.";
      Toast.fire({ icon: "error", title: msg });
    } finally {
      setActionLoading(false);
    }
  };

  // View transfer item details drill-down
  const viewTransferDetails = async (ref) => {
    setSelectedTransferRef(ref);
    setShowDetailsModal(true);
    setDetailsLoading(true);
    try {
      const resp = await axios.get(`${baseUrl}settlement/transfer_details`, {
        params: { reference: ref },
      });
      if (resp.data?.status === "success") {
        setTransferDetails(resp.data.data || []);
      } else {
        setTransferDetails([]);
      }
    } catch (err) {
      console.error("Failed to load transfer breakdown:", err);
      Toast.fire({ icon: "error", title: "Failed to load payment breakdown." });
    } finally {
      setDetailsLoading(false);
    }
  };

  // CSV Export for History
  const handleExportCSV = () => {
    const params = new URLSearchParams();
    if (historyFilters.method && historyFilters.method !== "all") {
      params.append("method", historyFilters.method);
    }
    if (historyFilters.status && historyFilters.status !== "all") {
      params.append("status", historyFilters.status);
    }
    if (historyFilters.account_id) {
      params.append("account_id", historyFilters.account_id);
    }
    if (historyFilters.search) {
      params.append("search", historyFilters.search);
    }

    const downloadUrl = `${baseUrl}settlement/history_csv?${params.toString()}`;
    window.open(downloadUrl, "_blank");
  };

  // Filtered exceptions from history
  const failedTransfers = useMemo(() => {
    return historyData.rows.filter(
      (r) =>
        r.status?.toLowerCase() === "failed" ||
        r.status?.toLowerCase() === "reversed"
    );
  }, [historyData.rows]);

  return (
    <div className="container-fluid py-4 px-md-4">
      {/* Header bar */}
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center mb-4 gap-3">
        <div>
          <h3 className="fw-bold mb-1 text-dark">
            <i className="fas fa-university text-primary me-2"></i>
            Settlement Management
          </h3>
          <p className="text-muted small mb-0">
            Unified disbursement engine: Manage Paystack automated settlements,
            resolve failure exceptions, and record manual bank transfers with
            built-in duplicate prevention.
          </p>
        </div>

        <div className="d-flex flex-wrap gap-2">
          <button
            className="btn btn-outline-secondary d-flex align-items-center gap-2"
            onClick={refreshAll}
            disabled={loading || actionLoading}
          >
            <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`}></i>
            Refresh
          </button>

          <button
            className="btn btn-outline-primary d-flex align-items-center gap-2"
            onClick={handleExportCSV}
          >
            <i className="fas fa-file-csv"></i>
            Export History
          </button>

          <button
            className="btn btn-warning text-dark d-flex align-items-center gap-2"
            onClick={() => handleTriggerAutomated(null, "All Accounts")}
            disabled={actionLoading || unsettledQueue.length === 0}
            title="Initiate Paystack transfers for all pending unsettled collections"
          >
            <i className="fas fa-bolt"></i>
            Run Automated Settlement
          </button>

          <button
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={openManualModalBlank}
            disabled={actionLoading}
          >
            <i className="fas fa-plus-circle"></i>
            Record Manual Transfer
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="row g-3 mb-4">
        {/* Unsettled Pending Balance */}
        <div className="col-12 col-sm-6 col-xl-3">
          <div
            className="card border-0 shadow-sm h-100 bg-white"
            style={{ cursor: "pointer", borderLeft: "4px solid #ffc107" }}
            onClick={() => setActiveTab("queue")}
          >
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted small fw-semibold text-uppercase">
                  Unsettled Balance
                </span>
                <span className="badge bg-warning text-dark rounded-pill">
                  {overview.unsettled_count} payments
                </span>
              </div>
              <h4 className="fw-bold mt-2 mb-1 text-dark">
                {formatMoney(overview.unsettled_amount)}
              </h4>
              <div className="small text-muted">
                Pending disbursement to destination accounts
              </div>
            </div>
          </div>
        </div>

        {/* Automated Settled (Paystack) */}
        <div className="col-12 col-sm-6 col-xl-3">
          <div
            className="card border-0 shadow-sm h-100 bg-white"
            style={{ borderLeft: "4px solid #0d6efd" }}
          >
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted small fw-semibold text-uppercase">
                  Automated Settled
                </span>
                <span className="badge bg-primary rounded-pill">Paystack</span>
              </div>
              <h4 className="fw-bold mt-2 mb-1 text-primary">
                {formatMoney(overview.automated_settled_amount)}
              </h4>
              <div className="small text-muted">
                Directly disbursed via Paystack API
              </div>
            </div>
          </div>
        </div>

        {/* Manually Settled (Bank Transfer) */}
        <div className="col-12 col-sm-6 col-xl-3">
          <div
            className="card border-0 shadow-sm h-100 bg-white"
            style={{ borderLeft: "4px solid #6f42c1" }}
          >
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted small fw-semibold text-uppercase">
                  Manually Settled
                </span>
                <span
                  className="badge text-white rounded-pill"
                  style={{ backgroundColor: "#6f42c1" }}
                >
                  Bank Transfer
                </span>
              </div>
              <h4 className="fw-bold mt-2 mb-1" style={{ color: "#6f42c1" }}>
                {formatMoney(overview.manual_settled_amount)}
              </h4>
              <div className="small text-muted">
                Bank transfers executed by Finance Officers
              </div>
            </div>
          </div>
        </div>

        {/* Failed / Exceptions */}
        <div className="col-12 col-sm-6 col-xl-3">
          <div
            className="card border-0 shadow-sm h-100 bg-white"
            style={{
              cursor: "pointer",
              borderLeft: `4px solid ${
                overview.failed_transfers_count > 0 ? "#dc3545" : "#198754"
              }`,
            }}
            onClick={() => setActiveTab("exceptions")}
          >
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted small fw-semibold text-uppercase">
                  Failed Exceptions
                </span>
                {overview.failed_transfers_count > 0 ? (
                  <span className="badge bg-danger rounded-pill">
                    Needs Action
                  </span>
                ) : (
                  <span className="badge bg-success rounded-pill">Healthy</span>
                )}
              </div>
              <h4
                className={`fw-bold mt-2 mb-1 ${
                  overview.failed_transfers_count > 0
                    ? "text-danger"
                    : "text-success"
                }`}
              >
                {overview.failed_transfers_count}
              </h4>
              <div className="small text-muted">
                Paystack failures requiring manual transfer
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <ul className="nav nav-pills mb-4 gap-2 bg-white p-2 rounded shadow-sm border">
        <li className="nav-item">
          <button
            className={`nav-link fw-semibold d-flex align-items-center gap-2 ${
              activeTab === "queue" ? "active" : "text-secondary"
            }`}
            onClick={() => setActiveTab("queue")}
          >
            <i className="fas fa-layer-group"></i>
            Unsettled Queue
            {unsettledQueue.length > 0 && (
              <span className="badge bg-warning text-dark ms-1">
                {unsettledQueue.length}
              </span>
            )}
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link fw-semibold d-flex align-items-center gap-2 ${
              activeTab === "exceptions" ? "active" : "text-secondary"
            }`}
            onClick={() => setActiveTab("exceptions")}
          >
            <i className="fas fa-exclamation-triangle"></i>
            Failed & Exceptions
            {overview.failed_transfers_count > 0 && (
              <span className="badge bg-danger ms-1">
                {overview.failed_transfers_count}
              </span>
            )}
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link fw-semibold d-flex align-items-center gap-2 ${
              activeTab === "history" ? "active" : "text-secondary"
            }`}
            onClick={() => setActiveTab("history")}
          >
            <i className="fas fa-history"></i>
            Unified Disbursement History
          </button>
        </li>
      </ul>

      {/* TAB 1: UNSETTLED QUEUE */}
      {activeTab === "queue" && (
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
            <div>
              <h5 className="fw-bold mb-0 text-dark">
                Pending Unsettled Collections by Destination Account
              </h5>
              <small className="text-muted">
                Payments collected from students awaiting automated Paystack
                disbursement or manual bank transfer.
              </small>
            </div>
            {unsettledQueue.length > 0 && (
              <button
                className="btn btn-sm btn-outline-warning text-dark fw-semibold"
                onClick={() => handleTriggerAutomated(null, "All Accounts")}
                disabled={actionLoading}
              >
                <i className="fas fa-bolt me-1"></i>
                Disburse All via Paystack
              </button>
            )}
          </div>

          <div className="card-body p-0">
            {unsettledQueue.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-check-circle text-success fa-3x mb-3"></i>
                <h5 className="fw-bold text-dark">All Collections Settled</h5>
                <p className="text-muted mb-0">
                  There are currently no unsettled student invoice payments
                  pending disbursement.
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light text-muted small text-uppercase">
                    <tr>
                      <th className="ps-3">Destination Account</th>
                      <th>Bank & Account Number</th>
                      <th>Paystack Recipient</th>
                      <th>Payment Count</th>
                      <th>Total Amount (NGN)</th>
                      <th>Status / Notes</th>
                      <th className="text-end pe-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unsettledQueue.map((group) => {
                      const hasRecipient = Boolean(group.recipient_code);
                      const hasFailed = Boolean(group.has_failed_attempt);

                      return (
                        <tr key={group.account_id}>
                          <td className="ps-3">
                            <div className="fw-bold text-dark">
                              {group.account_title}
                            </div>
                            <small className="text-muted">
                              Account ID: #{group.account_id}
                            </small>
                          </td>
                          <td>
                            <div className="fw-semibold text-secondary">
                              {group.bank_name || "N/A"}
                            </div>
                            <small className="text-muted font-monospace">
                              {group.account_number || "No account number"}
                            </small>
                          </td>
                          <td>
                            {hasRecipient ? (
                              <span className="badge bg-light text-primary border border-primary font-monospace">
                                {group.recipient_code}
                              </span>
                            ) : (
                              <span className="badge bg-warning text-dark">
                                <i className="fas fa-exclamation-circle me-1"></i>
                                Missing Recipient Code
                              </span>
                            )}
                          </td>
                          <td>
                            <span className="badge bg-secondary rounded-pill">
                              {group.payment_count} payments
                            </span>
                          </td>
                          <td>
                            <span className="fw-bold fs-6 text-dark">
                              {formatMoney(group.total_amount)}
                            </span>
                          </td>
                          <td>
                            {hasFailed ? (
                              <div>
                                <span className="badge bg-danger mb-1">
                                  <i className="fas fa-times-circle me-1"></i>
                                  Last Transfer Failed
                                </span>
                                <div
                                  className="text-danger small"
                                  style={{ maxWidth: "240px" }}
                                >
                                  {group.last_failed?.reason}
                                </div>
                              </div>
                            ) : hasRecipient ? (
                              <span className="badge bg-success-subtle text-success border border-success-subtle">
                                <i className="fas fa-check me-1"></i>Ready for
                                Automated Run
                              </span>
                            ) : (
                              <span className="badge bg-secondary-subtle text-secondary">
                                Requires Manual Transfer
                              </span>
                            )}
                          </td>
                          <td className="text-end pe-3">
                            <div className="d-flex justify-content-end gap-2">
                              {/* Automated Button */}
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() =>
                                  handleTriggerAutomated(
                                    group.account_id,
                                    group.account_title
                                  )
                                }
                                disabled={actionLoading || !hasRecipient}
                                title={
                                  hasRecipient
                                    ? "Disburse automatically via Paystack"
                                    : "Cannot trigger automated transfer without recipient code. Settle manually."
                                }
                              >
                                <i className="fas fa-robot me-1"></i>
                                Paystack
                              </button>

                              {/* Manual Transfer Button */}
                              <button
                                className="btn btn-sm btn-outline-dark"
                                style={{
                                  borderColor: "#6f42c1",
                                  color: "#6f42c1",
                                }}
                                onClick={() => openManualModalForQueueItem(group)}
                                disabled={actionLoading}
                                title="Record manual bank transfer after completing offline transfer"
                              >
                                <i className="fas fa-university me-1"></i>
                                Settle Manually
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 2: EXCEPTIONS & FAILED TRANSFERS */}
      {activeTab === "exceptions" && (
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
            <div>
              <h5 className="fw-bold mb-0 text-danger">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Failed Automated Settlements & Exceptions
              </h5>
              <small className="text-muted">
                Transfers that encountered errors at Paystack. You can retry
                automated disbursement or record a manual bank transfer to
                settle and unblock the records.
              </small>
            </div>
          </div>

          <div className="card-body p-0">
            {failedTransfers.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-shield-alt text-success fa-3x mb-3"></i>
                <h5 className="fw-bold text-dark">No Failed Settlements</h5>
                <p className="text-muted mb-0">
                  Great! There are no automated settlement failures or
                  unresolved transfer exceptions in the records.
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light text-muted small text-uppercase">
                    <tr>
                      <th className="ps-3">Date Attempted</th>
                      <th>Reference</th>
                      <th>Destination Account</th>
                      <th>Bank & Account Number</th>
                      <th>Amount (NGN)</th>
                      <th>Paystack Error / Reason</th>
                      <th className="text-end pe-3">Resolution Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failedTransfers.map((item) => {
                      let failMsg = "Automated transfer failed at Paystack";
                      try {
                        const parsed = JSON.parse(item.response || "{}");
                        failMsg =
                          parsed.message ||
                          parsed.data?.message ||
                          item.manual_notes ||
                          failMsg;
                      } catch (e) {
                        failMsg = item.manual_notes || failMsg;
                      }

                      return (
                        <tr key={item.id}>
                          <td className="ps-3 text-muted small">
                            {formatDate(item.created_at)}
                          </td>
                          <td>
                            <span className="font-monospace fw-semibold text-dark">
                              {item.transfer_reference}
                            </span>
                          </td>
                          <td>
                            <div className="fw-semibold text-dark">
                              {item.account_title}
                            </div>
                            <small className="text-muted">
                              Account ID: #{item.settlement_account_id}
                            </small>
                          </td>
                          <td>
                            <div>{item.destination_bank || "N/A"}</div>
                            <small className="text-muted font-monospace">
                              {item.destination_account_number || "N/A"}
                            </small>
                          </td>
                          <td>
                            <span className="fw-bold text-danger">
                              {formatMoney(item.total_amount)}
                            </span>
                          </td>
                          <td>
                            <div
                              className="text-danger small"
                              style={{ maxWidth: "260px" }}
                            >
                              <i className="fas fa-info-circle me-1"></i>
                              {failMsg}
                            </div>
                          </td>
                          <td className="text-end pe-3">
                            <div className="d-flex justify-content-end gap-2">
                              {/* Resolve via Manual Bank Transfer */}
                              <button
                                className="btn btn-sm btn-success d-flex align-items-center gap-1"
                                onClick={() => {
                                  setManualForm({
                                    account_id: String(
                                      item.settlement_account_id
                                    ),
                                    account_title: item.account_title || "",
                                    bank_name: item.destination_bank || "",
                                    account_number:
                                      item.destination_account_number || "",
                                    amount: String(item.total_amount || ""),
                                    bank_reference: "",
                                    transfer_reference:
                                      item.transfer_reference || "",
                                    notes: `Manual bank transfer resolving failed Paystack transfer (${item.transfer_reference}): ${failMsg}`,
                                    settled_by: officerEmail,
                                    transfer_date: new Date()
                                      .toISOString()
                                      .slice(0, 16),
                                    payment_ids: [],
                                  });
                                  setShowManualModal(true);
                                }}
                                title="Record manual bank transfer to resolve this failed run"
                              >
                                <i className="fas fa-check-circle"></i>
                                Switch to Manual
                              </button>

                              {/* Retry Automated */}
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() =>
                                  handleTriggerAutomated(
                                    item.settlement_account_id,
                                    item.account_title
                                  )
                                }
                                disabled={actionLoading}
                                title="Retry automated transfer"
                              >
                                <i className="fas fa-redo"></i>
                                Retry
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 3: UNIFIED DISBURSEMENT HISTORY */}
      {activeTab === "history" && (
        <div className="card border-0 shadow-sm">
          {/* History Filters */}
          <div className="card-header bg-white py-3 border-bottom">
            <div className="row g-2 align-items-center">
              {/* Search */}
              <div className="col-12 col-md-4">
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0">
                    <i className="fas fa-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control bg-light border-start-0"
                    placeholder="Search by ref, teller, account..."
                    value={historyFilters.search}
                    onChange={(e) =>
                      setHistoryFilters((prev) => ({
                        ...prev,
                        search: e.target.value,
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        fetchHistory({ offset: 0 });
                      }
                    }}
                  />
                </div>
              </div>

              {/* Method Filter */}
              <div className="col-6 col-md-2">
                <select
                  className="form-select"
                  value={historyFilters.method}
                  onChange={(e) => {
                    const method = e.target.value;
                    setHistoryFilters((prev) => ({ ...prev, method, offset: 0 }));
                    fetchHistory({ method, offset: 0 });
                  }}
                >
                  <option value="all">All Methods</option>
                  <option value="automated">Automated (Paystack)</option>
                  <option value="manual">Manual (Bank Transfer)</option>
                </select>
              </div>

              {/* Status Filter */}
              <div className="col-6 col-md-2">
                <select
                  className="form-select"
                  value={historyFilters.status}
                  onChange={(e) => {
                    const status = e.target.value;
                    setHistoryFilters((prev) => ({ ...prev, status, offset: 0 }));
                    fetchHistory({ status, offset: 0 });
                  }}
                >
                  <option value="all">All Statuses</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                  <option value="processing">Processing</option>
                  <option value="reversed">Reversed</option>
                </select>
              </div>

              {/* Account Filter */}
              <div className="col-6 col-md-2">
                <select
                  className="form-select"
                  value={historyFilters.account_id}
                  onChange={(e) => {
                    const account_id = e.target.value;
                    setHistoryFilters((prev) => ({
                      ...prev,
                      account_id,
                      offset: 0,
                    }));
                    fetchHistory({ account_id, offset: 0 });
                  }}
                >
                  <option value="">All Accounts</option>
                  {accounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.acc_title || acc.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Filter Apply / Reset */}
              <div className="col-6 col-md-2 d-flex gap-2">
                <button
                  className="btn btn-primary w-100"
                  onClick={() => fetchHistory({ offset: 0 })}
                  disabled={loading}
                >
                  Filter
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    const reset = {
                      method: "all",
                      status: "all",
                      account_id: "",
                      search: "",
                      offset: 0,
                      limit: 50,
                    };
                    setHistoryFilters(reset);
                    fetchHistory(reset);
                  }}
                  title="Reset filters"
                >
                  <i className="fas fa-undo"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div
                  className="spinner-border text-primary mb-2"
                  role="status"
                ></div>
                <div className="text-muted">Loading settlement records...</div>
              </div>
            ) : historyData.rows.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-inbox text-muted fa-3x mb-3"></i>
                <h5 className="fw-bold text-dark">No Settlement Records</h5>
                <p className="text-muted mb-0">
                  No disbursement records match the selected filter criteria.
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light text-muted small text-uppercase">
                    <tr>
                      <th className="ps-3">Date</th>
                      <th>Reference</th>
                      <th>Method</th>
                      <th>Destination Account</th>
                      <th>Bank & Account No</th>
                      <th>Amount (NGN)</th>
                      <th>Status</th>
                      <th>Bank Reference / Teller</th>
                      <th>Settled By</th>
                      <th className="text-end pe-3">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.rows.map((row) => {
                      const isManual =
                        row.settlement_method?.toLowerCase() === "manual";
                      const dateDisplay =
                        row.settled_at || row.created_at || "N/A";
                      const statusLower = (row.status || "").toLowerCase();

                      return (
                        <tr key={row.id}>
                          <td className="ps-3 text-muted small">
                            {formatDate(dateDisplay)}
                          </td>
                          <td>
                            <span className="font-monospace fw-semibold text-dark">
                              {row.transfer_reference || "N/A"}
                            </span>
                          </td>
                          <td>
                            {isManual ? (
                              <span
                                className="badge text-white px-2 py-1"
                                style={{ backgroundColor: "#6f42c1" }}
                              >
                                <i className="fas fa-university me-1"></i>Manual
                              </span>
                            ) : (
                              <span className="badge bg-primary px-2 py-1">
                                <i className="fas fa-robot me-1"></i>Paystack
                              </span>
                            )}
                          </td>
                          <td>
                            <div className="fw-semibold text-dark">
                              {row.account_title || "Unknown"}
                            </div>
                          </td>
                          <td>
                            <div>
                              {row.destination_bank ||
                                row.manual_bank_name ||
                                "N/A"}
                            </div>
                            <small className="text-muted font-monospace">
                              {row.destination_account_number || "N/A"}
                            </small>
                          </td>
                          <td>
                            <span className="fw-bold text-dark">
                              {formatMoney(row.total_amount)}
                            </span>
                          </td>
                          <td>
                            {statusLower === "success" && (
                              <span className="badge bg-success">Success</span>
                            )}
                            {statusLower === "failed" && (
                              <span className="badge bg-danger">Failed</span>
                            )}
                            {statusLower === "processing" && (
                              <span className="badge bg-warning text-dark">
                                Processing
                              </span>
                            )}
                            {statusLower === "reversed" && (
                              <span className="badge bg-secondary">
                                Reversed
                              </span>
                            )}
                            {!["success", "failed", "processing", "reversed"].includes(
                              statusLower
                            ) && (
                              <span className="badge bg-light text-dark border">
                                {row.status || "Unknown"}
                              </span>
                            )}
                          </td>
                          <td>
                            {row.manual_bank_reference ? (
                              <span className="font-monospace text-dark fw-semibold">
                                {row.manual_bank_reference}
                              </span>
                            ) : row.transfer_code ? (
                              <span className="font-monospace text-muted small">
                                {row.transfer_code}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            <small className="text-muted">
                              {row.settled_by ||
                                (isManual ? "Finance Officer" : "Automated System")}
                            </small>
                          </td>
                          <td className="text-end pe-3">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() =>
                                viewTransferDetails(row.transfer_reference)
                              }
                              title="View itemized student payment breakdown"
                            >
                              <i className="fas fa-list-ul me-1"></i>
                              Items
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* History Pagination */}
          {historyData.total > historyData.limit && (
            <div className="card-footer bg-white py-3 border-top d-flex justify-content-between align-items-center">
              <small className="text-muted">
                Showing {historyData.offset + 1} to{" "}
                {Math.min(
                  historyData.offset + historyData.limit,
                  historyData.total
                )}{" "}
                of {historyData.total} records
              </small>

              <div className="btn-group">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={historyData.offset <= 0}
                  onClick={() => {
                    const newOffset = Math.max(
                      0,
                      historyData.offset - historyData.limit
                    );
                    setHistoryFilters((prev) => ({
                      ...prev,
                      offset: newOffset,
                    }));
                    fetchHistory({ offset: newOffset });
                  }}
                >
                  <i className="fas fa-chevron-left me-1"></i>Previous
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={
                    historyData.offset + historyData.limit >= historyData.total
                  }
                  onClick={() => {
                    const newOffset =
                      historyData.offset + historyData.limit;
                    setHistoryFilters((prev) => ({
                      ...prev,
                      offset: newOffset,
                    }));
                    fetchHistory({ offset: newOffset });
                  }}
                >
                  Next<i className="fas fa-chevron-right ms-1"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* MODAL 1: RECORD MANUAL SETTLEMENT */}
      {showManualModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              <div className="modal-header bg-light border-bottom">
                <h5 className="modal-title fw-bold text-dark">
                  <i
                    className="fas fa-university me-2"
                    style={{ color: "#6f42c1" }}
                  ></i>
                  Record Manual Bank Transfer Settlement
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowManualModal(false)}
                ></button>
              </div>

              <form onSubmit={handleSubmitManualSettlement}>
                <div className="modal-body p-4">
                  {/* Informational Alert */}
                  <div className="alert alert-info border-0 shadow-sm d-flex align-items-start gap-2 mb-4">
                    <i className="fas fa-shield-alt fs-5 mt-1 text-primary"></i>
                    <div>
                      <strong>Automated Cron Synchronization Guarantee:</strong>
                      <div className="small">
                        Recording this manual transfer will permanently mark
                        the associated payments as <code>settled = 'yes'</code>.
                        The automated Paystack scheduler will{" "}
                        <strong>never</strong> re-transfer or double-pay these
                        items.
                      </div>
                    </div>
                  </div>

                  <div className="row g-3">
                    {/* Destination Account */}
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold">
                        Destination Settlement Account{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={manualForm.account_id}
                        onChange={(e) =>
                          handleAccountSelectChange(e.target.value)
                        }
                        required
                      >
                        <option value="">-- Select Account --</option>
                        {accounts.map((acc) => (
                          <option key={acc.id} value={acc.id}>
                            {acc.acc_title || acc.name} ({acc.bank_name} -{" "}
                            {acc.account_number})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Amount */}
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold">
                        Transfer Amount (NGN){" "}
                        <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">₦</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          className="form-control"
                          placeholder="e.g. 50000.00"
                          value={manualForm.amount}
                          onChange={(e) =>
                            setManualForm((prev) => ({
                              ...prev,
                              amount: e.target.value,
                            }))
                          }
                          required
                        />
                      </div>
                    </div>

                    {/* Destination Bank Name */}
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold">
                        Destination Bank Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Stanbic IBTC Bank"
                        value={manualForm.bank_name}
                        onChange={(e) =>
                          setManualForm((prev) => ({
                            ...prev,
                            bank_name: e.target.value,
                          }))
                        }
                      />
                    </div>

                    {/* Bank Reference / Teller Number */}
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold">
                        Bank Reference / Teller No / Session ID{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control font-monospace"
                        placeholder="e.g. STANBIC-TRF-098231"
                        value={manualForm.bank_reference}
                        onChange={(e) =>
                          setManualForm((prev) => ({
                            ...prev,
                            bank_reference: e.target.value,
                          }))
                        }
                        required
                      />
                      <small className="text-muted">
                        Official bank transfer session ID, teller, or receipt
                        number.
                      </small>
                    </div>

                    {/* Associated Failed Reference (if applicable) */}
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold">
                        Failed Transfer Reference (Optional)
                      </label>
                      <input
                        type="text"
                        className="form-control font-monospace"
                        placeholder="e.g. TRF-..."
                        value={manualForm.transfer_reference}
                        onChange={(e) =>
                          setManualForm((prev) => ({
                            ...prev,
                            transfer_reference: e.target.value,
                          }))
                        }
                      />
                      <small className="text-muted">
                        If resolving a failed automated Paystack transfer,
                        enter its reference.
                      </small>
                    </div>

                    {/* Transfer Date / Time */}
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold">
                        Date & Time of Transfer
                      </label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={manualForm.transfer_date}
                        onChange={(e) =>
                          setManualForm((prev) => ({
                            ...prev,
                            transfer_date: e.target.value,
                          }))
                        }
                      />
                    </div>

                    {/* Settled By */}
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold">
                        Settled By (Finance Officer)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={manualForm.settled_by}
                        onChange={(e) =>
                          setManualForm((prev) => ({
                            ...prev,
                            settled_by: e.target.value,
                          }))
                        }
                      />
                    </div>

                    {/* Notes */}
                    <div className="col-12">
                      <label className="form-label fw-semibold">
                        Audit Notes & Justification
                      </label>
                      <textarea
                        className="form-control"
                        rows="2"
                        placeholder="Reason for manual transfer (e.g. Paystack balance depleted, bank network error, etc.)"
                        value={manualForm.notes}
                        onChange={(e) =>
                          setManualForm((prev) => ({
                            ...prev,
                            notes: e.target.value,
                          }))
                        }
                      ></textarea>
                    </div>
                  </div>
                </div>

                <div className="modal-footer bg-light border-top">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowManualModal(false)}
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={actionLoading}
                    style={{ backgroundColor: "#6f42c1", borderColor: "#6f42c1" }}
                  >
                    {actionLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        ></span>
                        Recording...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check-circle me-1"></i>
                        Confirm & Record Settlement
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: TRANSFER PAYMENT ITEMS BREAKDOWN */}
      {showDetailsModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              <div className="modal-header bg-light border-bottom">
                <div>
                  <h5 className="modal-title fw-bold text-dark mb-0">
                    <i className="fas fa-file-invoice-dollar text-primary me-2"></i>
                    Itemized Payment Breakdown
                  </h5>
                  <small className="text-muted font-monospace">
                    Transfer Reference: {selectedTransferRef}
                  </small>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDetailsModal(false)}
                ></button>
              </div>

              <div className="modal-body p-4">
                {detailsLoading ? (
                  <div className="text-center py-5">
                    <div
                      className="spinner-border text-primary mb-2"
                      role="status"
                    ></div>
                    <div className="text-muted">
                      Loading payment item details...
                    </div>
                  </div>
                ) : transferDetails.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="fas fa-info-circle text-muted fa-3x mb-3"></i>
                    <h6 className="fw-bold">No Itemized Records Found</h6>
                    <p className="text-muted mb-0">
                      Payment item details were not recorded or this transfer was
                      processed under legacy mode.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="text-muted small">
                        Total {transferDetails.length} reconciled student
                        payments
                      </span>
                      <span className="fw-bold fs-6 text-dark">
                        Total:{" "}
                        {formatMoney(
                          transferDetails.reduce(
                            (acc, i) => acc + Number(i.amount || 0),
                            0
                          )
                        )}
                      </span>
                    </div>

                    <div className="table-responsive">
                      <table className="table table-sm table-hover align-middle mb-0">
                        <thead className="table-light text-muted small text-uppercase">
                          <tr>
                            <th>#</th>
                            <th>Student Email</th>
                            <th>Invoice Code</th>
                            <th>Fee Item</th>
                            <th>Amount Paid</th>
                            <th>Payment Date</th>
                            <th>Settlement Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transferDetails.map((item, idx) => (
                            <tr key={item.settlement_item_id || idx}>
                              <td className="text-muted small">{idx + 1}</td>
                              <td className="fw-semibold">
                                {item.student_email || "N/A"}
                              </td>
                              <td className="font-monospace small">
                                {item.invoice_code || "N/A"}
                              </td>
                              <td>{item.item_title || "Fee Item"}</td>
                              <td className="fw-bold">
                                {formatMoney(item.amount)}
                              </td>
                              <td className="text-muted small">
                                {formatDate(item.paid_at)}
                              </td>
                              <td>
                                <span className="badge bg-success-subtle text-success">
                                  {item.settlement_status || "Settled"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer bg-light border-top">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettlementManagement;

